import { useEffect } from "react";

export const SITE_URL = "https://thebirthlife.com";
export const SITE_NAME = "The Birth Life";
export const DEFAULT_TITLE = "Lactation Consultant in Gurgaon & Online | The Birth Life — Yogyata Wadhwa, IBCLC";
export const DEFAULT_DESCRIPTION =
  "Evidence-based lactation consulting, childbirth education and infant feeding support by Yogyata Wadhwa, IBCLC. In-clinic in Gurgaon & online worldwide — 20,000+ families supported.";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/yogyata-banner.jpg`;

const abs = (u) => (u && !u.startsWith("http") ? SITE_URL + (u.startsWith("/") ? u : "/" + u) : u);

const strip = (s) => {
  if (!s) return "";
  const d = new DOMParser().parseFromString(String(s), "text/html");
  return (d.body.textContent || "").replace(/\s+/g, " ").trim();
};

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.seoManaged = "true";
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.dataset.seoManaged = "true";
};

const applyMeta = ({ fullTitle, desc, url, img, type, noindex }) => {
  document.title = fullTitle;
  upsertMeta("name", "description", desc);
  upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1");
  upsertCanonical(url);
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:title", fullTitle);
  upsertMeta("property", "og:description", desc);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:image", img);
  upsertMeta("property", "og:locale", "en_IN");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", fullTitle);
  upsertMeta("name", "twitter:description", desc);
  upsertMeta("name", "twitter:image", img);
};

export const Seo = ({ title, description, path = "/", image, type = "website", jsonLd, noindex = false }) => {
  useEffect(() => {
    const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const url = SITE_URL + path;
    const img = image ? abs(image) : DEFAULT_OG_IMAGE;

    applyMeta({ fullTitle, desc, url, img, type, noindex });

    // Replace the static JSON-LD baked into the prerendered HTML (kept for
    // no-JS crawlers) with the live client-side schemas — avoids duplicates.
    document.querySelectorAll('script[type="application/ld+json"]:not([data-seo])').forEach((s) => s.remove());

    const items = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    items.forEach((obj) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "page";
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
    });

    return () => {
      document.title = DEFAULT_TITLE;
      applyMeta({ fullTitle: DEFAULT_TITLE, desc: DEFAULT_DESCRIPTION, url: SITE_URL + "/", img: DEFAULT_OG_IMAGE, type: "website", noindex: false });
      document.querySelectorAll('script[data-seo="page"]').forEach((s) => s.remove());
    };
  }, [title, description, path, image, type, jsonLd, noindex]);
  return null;
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.label,
    item: SITE_URL + item.href,
  })),
});

export const articleLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt || "",
  image: [abs(post.cover_image) || DEFAULT_OG_IMAGE],
  author: { "@id": SITE_URL + "/#yogyata" },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: SITE_URL + "/assets/logo-circle.png" },
  },
  ...(post.published_at ? { datePublished: post.published_at, dateModified: post.updated_at || post.published_at } : {}),
  inLanguage: "en",
  mainEntityOfPage: { "@type": "WebPage", "@id": SITE_URL + "/blog/" + post.slug + "/" },
});

export const blogLd = (posts) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "The Birth Life Journal",
  url: SITE_URL + "/blog/",
  blogPost: posts.map((p) => ({
    "@type": "BlogPosting",
    headline: p.title,
    description: p.excerpt || "",
    url: SITE_URL + "/blog/" + p.slug + "/",
    ...(p.published_at ? { datePublished: p.published_at } : {}),
    author: { "@type": "Person", name: "Yogyata Wadhwa" },
  })),
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL + "/",
  inLanguage: "en",
  publisher: { "@type": "Organization", name: SITE_NAME },
});

export const organizationLd = (settings = {}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL + "/",
  logo: { "@type": "ImageObject", url: SITE_URL + "/assets/logo-circle.png" },
  ...(settings.email ? { email: settings.email } : {}),
  ...(settings.phone_display ? { telephone: settings.phone_display } : {}),
  sameAs: [settings.instagram_url].filter(Boolean),
});

export const BUSINESS_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "419, Fourth Floor, Eros City Square, Sector 49",
  addressLocality: "Gurugram",
  addressRegion: "Haryana",
  postalCode: "122018",
  addressCountry: "IN",
};

export const personLd = (settings = {}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": SITE_URL + "/#yogyata",
  name: "Yogyata Wadhwa",
  jobTitle: "International Board Certified Lactation Consultant (IBCLC) & Paediatric Feeding Therapist",
  worksFor: { "@id": SITE_URL + "/#business" },
  url: SITE_URL + "/",
  image: DEFAULT_OG_IMAGE,
  sameAs: [settings.instagram_url].filter(Boolean),
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "International Board Certified Lactation Consultant (IBCLC)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "Bachelor of Physiotherapy (BPT)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "SOS Approach to Feeding" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "IYCF Counselling (BPNI)" },
  ],
  memberOf: [
    { "@type": "Organization", name: "GOLD Learning" },
    { "@type": "Organization", name: "Association of Lactation Professionals India (ALPI)" },
  ],
  knowsAbout: ["Lactation consulting", "Breastfeeding", "Childbirth education", "Infant feeding", "Starting solids", "Postpartum care", "Tongue tie assessment", "Infant oral motor assessment", "Paediatric feeding therapy", "SOS Approach to Feeding", "Cervical auscultation", "Hypnobirthing"],
  address: BUSINESS_ADDRESS,
});

export const localBusinessLd = (settings = {}, services = []) => ({
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "@id": SITE_URL + "/#business",
  name: SITE_NAME,
  url: SITE_URL + "/",
  image: DEFAULT_OG_IMAGE,
  description: settings.hero_subtext || DEFAULT_DESCRIPTION,
  ...(settings.email ? { email: settings.email } : {}),
  ...(settings.phone_display ? { telephone: settings.phone_display } : {}),
  address: BUSINESS_ADDRESS,
  areaServed: ["Gurugram", "Delhi", "India", "United States", "United Kingdom"].map((a) => ({ "@type": "Place", name: a })),
  founder: { "@id": SITE_URL + "/#yogyata" },
  potentialAction: {
    "@type": "ReserveAction",
    name: "Book a consultation",
    target: { "@type": "EntryPoint", urlTemplate: SITE_URL + "/#services" },
    provider: { "@id": SITE_URL + "/#business" },
  },
  availableService: services
    .filter((s) => s.title)
    .map((s) => ({
      "@type": "Service",
      name: s.title,
      description: strip(s.desc),
      ...(s.price
        ? { offers: { "@type": "Offer", price: String(s.price).replace(/[₹,]/g, "").trim(), priceCurrency: "INR" } }
        : {}),
    })),
});

export const faqPageLd = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs
    .filter((f) => f.question)
    .map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer || "" },
    })),
});
